import { createContext, useEffect, useState, useCallback, useContext, useRef } from 'react';
import { syncIdentityAndProfile, clearSocketAuthentication, enableSocketAuthentication } from '../services/socket';
import { API_BASE, authenticatedRequest, requestServerLogout, requestAccountDeletion, broadcastAuthLogout } from '../services/authApi';
import { presenceDiag } from '../utils/presenceDiag';
import { tokenStore } from '../platform/tokenStore';
import { emitAuthRestoreStage, revokeCurrentPushDevice } from '../platform/pushNotifications';

// --- JWT guard: avoid persisting non-JWT (e.g. JSON strings) as token
function _isLikelyJwt(raw) {
  const s = String(raw || '').trim().replace(/^Bearer\s+/i, '');
  // Reject obvious non-tokens
  if (!s) return false;
  if (s.startsWith('{') || s.startsWith('[')) return false;
  if (s.includes(' ') || s.includes('\n') || s.includes('\t')) return false;
  // JWT should be 3 dot-separated base64url-ish parts
  const parts = s.split('.');
  if (parts.length !== 3) return false;
  // Basic base64url charset check (len>=1 each part)
  const re = /^[A-Za-z0-9_=-]+$/;
  return parts.every((p) => p && re.test(p));
}

function _cleanJwt(raw) {
  const s = String(raw || '').trim().replace(/^Bearer\s+/i, '').trim();
  return _isLikelyJwt(s) ? s : '';
}

export const AuthContext = createContext({
  token: null,
  user: null,
  isAuthReady: false,
  login: () => {},
  logout: () => {},
  deleteAccount: async () => {},
  refreshProfile: async () => {},
});

const USER_KEY = 'user';
const LOGOUT_BROADCAST_KEY = 'z10:auth_logout';

const safeGet = (key) => {
  try { return localStorage.getItem(key); } catch { return null; }
};

function clearBrowserAuthState() {
  try {
    ['token', 'accessToken', 'authToken', 'jwt', 'user', 'me', 'userId'].forEach((key) => localStorage.removeItem(key));
    window.dispatchEvent(new CustomEvent('token:changed', { detail: null }));
  } catch {}
  try {
    [
      'userIdSnapshot', 'profileSnapshot', 'socketProfileSnapshot',
      'csb_presence', 'csb_unread', 'csb_lastread', 'activeConvPeerId',
      'z10_active_conv',
    ].forEach((key) => sessionStorage.removeItem(key));
  } catch {}
}

const sanitizeUser = (raw) => {
  if (!raw) return null;
  const { _id, id, fullName, email, zodiacSign, photoPath, photoPublicId, bio, gender, emailVerified } = raw;
  return {
    _id: _id || id || null,
    fullName,
    email,
    zodiacSign,
    photoPath,
    photoPublicId,
    bio,
    gender,
    emailVerified,
  };
};

export const AuthProvider = ({ children }) => {
  const [token, setToken] = useState(null);
  const [user, setUser]   = useState(null);
  // Protected screens may render optimistically from storage. This flag keeps
  // their authenticated reads paused until that storage bootstrap finishes.
  const [isAuthReady, setIsAuthReady] = useState(false);
  const authGenerationRef = useRef(0);
  const authPhaseRef = useRef('anonymous');

  // This is intentionally idempotent: every forced-session path (manual
  // logout, refresh invalidation, socket invalidation, and another-tab
  // logout) reaches the same local cleanup without issuing another request.
  const performLocalAuthCleanup = useCallback(({ userId, reason = 'session_invalidated', broadcast = false } = {}) => {
    authPhaseRef.current = 'logging_out';
    authGenerationRef.current += 1;
    presenceDiag('auth-context:cleanup:start', { source: 'AuthContext.performLocalAuthCleanup', userId, reason });
    clearSocketAuthentication({ userId, reason });
    presenceDiag('auth-context:cleanup:after-socket', { source: 'AuthContext.performLocalAuthCleanup', userId, reason });
    tokenStore.clearAccessTokenImmediate();
    clearBrowserAuthState();
    setToken(null);
    setUser(null);
    authPhaseRef.current = 'anonymous';
    if (broadcast) broadcastAuthLogout();
    return tokenStore.clearAccessToken().catch(() => false);
  }, []);

  // Cargar del storage al montar
  useEffect(() => {
    let cancelled = false;
    emitAuthRestoreStage('provider_mounted');
    emitAuthRestoreStage('persistence_probe_started');
    (async () => {
      let loaded = null;
      let persistenceFailed = false;
      let restoreFailed = false;
      try { loaded = await tokenStore.loadAccessToken(); } catch { persistenceFailed = true; }
      if (cancelled) return;
      const t = _cleanJwt(loaded);
      const u = safeGet(USER_KEY);
      const parsed = (() => { try { return u ? JSON.parse(u) : null; } catch { return null; } })();
      const userId = parsed?._id || parsed?.id || parsed?.userId || null;
      if (persistenceFailed) {
        emitAuthRestoreStage('restore_failed');
      } else if (t) {
        emitAuthRestoreStage('persistence_found');
        setToken(t);
        if (userId) {
          authGenerationRef.current += 1;
          authPhaseRef.current = 'authenticated';
          enableSocketAuthentication({ token: t, userId, authGeneration: authGenerationRef.current });
        }
        presenceDiag('auth-context:restore-token', { source: 'AuthContext bootstrap' });
      } else if (loaded) {
        await tokenStore.clearAccessToken().catch(() => false);
        restoreFailed = true;
        emitAuthRestoreStage('restore_failed');
      } else {
        emitAuthRestoreStage('persistence_absent');
      }
      if (parsed) {
        setUser(parsed);
        if (userId) {
          try { syncIdentityAndProfile({ userId, ...parsed }); } catch {}
          presenceDiag('auth-context:restore-sync-socket', { source: 'AuthContext bootstrap', userId });
        }
      }
      if (!persistenceFailed && !restoreFailed) emitAuthRestoreStage(parsed && t ? 'restore_succeeded' : 'restore_unauthenticated');
      emitAuthRestoreStage('user_state_ready');
      setIsAuthReady(true);
      emitAuthRestoreStage('auth_ready');
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    const clearFromExternalInvalidation = () => {
      presenceDiag('auth-context:external-invalidation', { source: 'z10:authInvalidated/sessionInvalidated listener', reason: 'session_invalidated' });
      performLocalAuthCleanup({ reason: 'session_invalidated' });
    };
    const onStorage = (event) => {
      if (event?.key === LOGOUT_BROADCAST_KEY) clearFromExternalInvalidation();
    };
    window.addEventListener('storage', onStorage);
    window.addEventListener('z10:authInvalidated', clearFromExternalInvalidation);
    window.addEventListener('z10:sessionInvalidated', clearFromExternalInvalidation);
    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener('z10:authInvalidated', clearFromExternalInvalidation);
      window.removeEventListener('z10:sessionInvalidated', clearFromExternalInvalidation);
    };
  }, [performLocalAuthCleanup]);

  useEffect(() => {
    const onTokenRefreshed = (event) => {
      const refreshedToken = _cleanJwt(event?.detail || tokenStore.getAccessToken());
      if (refreshedToken && authPhaseRef.current === 'authenticated') setToken(refreshedToken);
    };
    window.addEventListener('auth:tokenRefreshed', onTokenRefreshed);
    return () => window.removeEventListener('auth:tokenRefreshed', onTokenRefreshed);
  }, []);

  const login = useCallback(async (tok, usr) => {
    const cleanTok = _cleanJwt(tok);

    if (cleanTok) {
      const userId = usr?._id || usr?.id || usr?.userId || null;
      await tokenStore.setAccessToken(cleanTok);
      authGenerationRef.current += 1;
      authPhaseRef.current = 'authenticated';
      setToken(cleanTok);
      presenceDiag('auth-context:login-token-persisted', { source: 'AuthContext.login', userId: usr?._id || usr?.id || usr?.userId || null });
      if (userId) enableSocketAuthentication({ token: cleanTok, userId, authGeneration: authGenerationRef.current });
    } else {
      // Never persist non-JWT values as token
      setToken(null);
      await tokenStore.clearAccessToken().catch(() => false);
      // eslint-disable-next-line no-console
      console.warn('[Auth] Ignoring non-JWT token value (not persisted).');
    }

    const safeUser = sanitizeUser(usr);
    setUser(safeUser);
    setIsAuthReady(true);
    try { localStorage.setItem(USER_KEY, JSON.stringify(safeUser || {})); } catch {}

    // Informar identidad al socket de inmediato
    try {
      const userId = usr?._id || usr?.id || usr?.userId;
      if (userId) syncIdentityAndProfile({ userId, ...usr });
      if (userId) presenceDiag('auth-context:login-sync-socket', { source: 'AuthContext.login', userId });
    } catch {}
  }, []);

  const logout = useCallback(async () => {
    // Capturar el userId ANTES de limpiar estado/storage
    const uid = user?._id || user?.id || user?.userId || null;
    const logoutToken = token || tokenStore.getAccessToken() || null;
    await revokeCurrentPushDevice().catch(() => false);
    // This is intentionally synchronous: no old token/user remains observable
    // while the optional server request is in flight.
    const storageCleanup = performLocalAuthCleanup({ userId: uid, reason: 'logout', broadcast: true });
    try {
      await requestServerLogout({ token: logoutToken });
    } catch {}
    await storageCleanup;
  }, [performLocalAuthCleanup, token, user]);

  const deleteAccount = useCallback(async () => {
    const uid = user?._id || user?.id || user?.userId || null;
    const deletionToken = token || tokenStore.getAccessToken() || null;
    if (!uid || !deletionToken) throw new Error('account_deletion_requires_authentication');

    // Server authority is removed first. A failed request must not strand the
    // user in a locally logged-out state while the account still exists.
    await requestAccountDeletion({ token: deletionToken });
    await performLocalAuthCleanup({ userId: uid, reason: 'account_deleted', broadcast: true });
    return true;
  }, [performLocalAuthCleanup, token, user]);

  const refreshProfile = useCallback(async () => {
    const generation = authGenerationRef.current;
    try {
      const t = _cleanJwt(token || tokenStore.getAccessToken());
      if (!t || authPhaseRef.current !== 'authenticated') return null;

      const res = await authenticatedRequest(`${API_BASE}/api/auth/profile`, {
        rawResponse: true,
        credentials: 'include',
        headers: t ? { Authorization: `Bearer ${t}` } : {},
        diagSource: 'AuthContext.refreshProfile',
      });

      if (!res.ok) {
        return null;
      }

      const newToken = res.headers.get('x-auth-token');
      if (generation !== authGenerationRef.current || authPhaseRef.current !== 'authenticated') return null;
      if (newToken && _cleanJwt(newToken)) {
        await tokenStore.setAccessToken(newToken);
        setToken(newToken);
      }

      let profile;
      try {
        profile = await res.json();
      } catch (e) {
        console.warn('[Auth] refreshProfile JSON inválido:', e?.message);
        return null;
      }

      const safeProfile = sanitizeUser(profile);

      setUser(prev => {
        if (generation !== authGenerationRef.current || authPhaseRef.current !== 'authenticated') return prev;
        const merged = { ...(prev || {}), ...(safeProfile || {}) };
        try { localStorage.setItem(USER_KEY, JSON.stringify(merged)); } catch {}
        return merged;
      });

      return safeProfile;
    } catch (e) {
      console.warn('[Auth] refreshProfile falló:', e?.message);
      return null;
    }
  }, [token]);

  return (
    <AuthContext.Provider value={{ token, user, isAuthReady, login, logout, deleteAccount, refreshProfile }}>
      {children}
    </AuthContext.Provider>
  );
};

// Hook de conveniencia para consumir el contexto de auth
export const useAuth = () => {
  return useContext(AuthContext);
};
